import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Drawer,
  Badge,
  LinearProgress,
  Pagination,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import TableChartIcon from '@mui/icons-material/TableChart';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import RestoreIcon from '@mui/icons-material/Restore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckpointIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { toast } from 'react-toastify';
import 'highlight.js/styles/github.css';
import { agentApi } from '../../services/api';
import type { AgentDetail, ConversationSession, ConversationSessionListItem, MessageAttachment, RestoreSnapshotOptions } from '../../types/agent';
import AIBuilderInsightsPanel from './AIBuilderInsightsPanel';
import { useAIBuilderExecution } from '../../hooks';
import useVoiceChat from '../../hooks/useVoiceChat';
import SubAgentEventsTree from '../chat/SubAgentEventsTree';
import AttachmentMessage from '../chat/AttachmentMessage';
import ConversationDetailsButton from '../chat/ConversationDetailsButton';
import ToolCallReason from '../chat/ToolCallReason';
import { getTextDirection, muiBidiTextSx } from '../chat/textDirection';
import {
  attachSubAgentEvent,
  flushPendingSubAgentEvents,
  markSubAgentEventsStopped,
} from '../../utils/subAgentEvents';
import {
  applyToolResultToMessages,
  bufferPendingToolResult,
  clearPendingToolResults,
  flushPendingToolResults,
  hasPendingToolCallMessages,
  mergePersistedToolResults,
  reconcileCompletionSessionMessages,
} from '../../utils/toolCallEvents';
import { MicButton, VoiceOverlay, VoiceSettings } from '../voice';
import stripMarkdown from '../../utils/stripMarkdown';
import {
  canAgentAcceptAudio,
  canAgentSpeakAudio,
  getAgentAudioInputDisabledReason,
} from '../../utils/audioInputSupport';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';

const MESSAGE_PAGE_SIZE = 10;
const AUTO_SCROLL_BOTTOM_THRESHOLD = 96;
const buildCompactSessionPageRequest = (
  overrides: { beforeMessageId?: number | null } = {},
) => ({
  messageLimit: MESSAGE_PAGE_SIZE,
  includeExecutions: false,
  includeSystemSnapshot: false,
  compactToolOutputs: true,
  ...overrides,
});
const DEFAULT_DATA_MODIFYING_TOOL_NAMES = [
  'create_ai_system',
  'update_ai_system',
  'update_system',
  'delete_ai_system',
  'create_expert',
  'update_expert',
  'delete_expert',
  'create_ai_schedule',
  'update_ai_schedule',
  'delete_ai_schedule',
  'create_agent_schedule',
  'update_agent_schedule',
  'delete_agent_schedule',
  'trigger_agent_schedule',
  'toggle_agent_schedule',
];

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to get icon for attachment type
const getAttachmentIcon = (type: string) => {
  switch (type) {
    case 'IMAGE': return <ImageIcon fontSize="small" />;
    case 'DOCUMENT': return <DescriptionIcon fontSize="small" />;
    case 'CODE': return <CodeIcon fontSize="small" />;
    case 'DATA': return <TableChartIcon fontSize="small" />;
    case 'AUDIO': return <AudiotrackIcon fontSize="small" />;
    default: return <InsertDriveFileIcon fontSize="small" />;
  }
};

// Helper function to clean markdown from session titles
const cleanMarkdownTitle = (title: string): string => {
  if (!title) return title;
  
  // Remove markdown headers (###, ##, #)
  let cleaned = title.replace(/^#{1,6}\s+/gm, '');
  
  // Remove bold/italic markers
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1'); // **bold**
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1'); // *italic*
  cleaned = cleaned.replace(/__([^_]+)__/g, '$1'); // __bold__
  cleaned = cleaned.replace(/_([^_]+)_/g, '$1'); // _italic_
  
  // Remove code blocks
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1'); // `code`
  
  // Remove links but keep text [text](url)
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  return cleaned.trim();
};

interface AIBuilderProps {
  system: any; // AI System data
  agent: AgentDetail; // System builder agent
  onExpertCreated?: () => void | Promise<void>; // Callback when AI makes any changes (experts, schedules, system config, etc.)
  headerActions?: React.ReactNode; // Optional custom actions to render in header (e.g., agent selector)
  chatOnly?: boolean; // Render only the shared builder chat pane
  chatTitle?: string;
  assistantLabel?: string;
  emptyStateTitle?: string;
  emptyStateHint?: string;
  inputPlaceholder?: string;
  initialMessagePrefix?: string;
  executionSystemId?: number | null;
  executionContext?: Record<string, any>;
  sessionAiSystemId?: number | null;
  sessionQueryParams?: Record<string, string | number | boolean | null | undefined>;
  autoLoadLatestSession?: boolean;
  enableSessionHistory?: boolean;
  enableSnapshots?: boolean;
  refreshToolNames?: string[];
  // Canvas props for embedding interactive canvas in insights panel
  nodes?: any[];
  edges?: any[];
  onNodesChange?: any;
  onEdgesChange?: any;
  onConnect?: any;
  onNodeClick?: (event: React.MouseEvent, node: any) => void;
  onAutoLayout?: () => void;
  expertsData?: any;
  addExpertToCanvas?: (expert: any) => void;
  setExpertFormOpen?: (open: boolean) => void;
  selectedNode?: any;
  setSelectedNode?: (node: any) => void;
  setNodes?: React.Dispatch<React.SetStateAction<any[]>>;
  setEdges?: React.Dispatch<React.SetStateAction<any[]>>;
  refetchExperts?: () => void;
  validationIssues?: any[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  onStopCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  isStoppingCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, any>;
  onViewExpertRun?: (expertId: number) => void;
  nodeTypes?: any; // Custom node types for ReactFlow
  onExpertUpdate?: (expert: any | null) => void; // Callback when expert is updated
}

interface Message {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL_CALL' | 'ASSISTANT_STATUS';
  content: string;
  timestamp: Date;
  executionId?: string; // Track execution ID for each assistant message
  toolNames?: string[];
  voiceReady?: boolean;
  snapshotMode?: 'NONE' | 'FULL' | 'LIGHTWEIGHT' | 'DIFF';
  hasSnapshot?: boolean;
  dbMessageId?: number; // Database ID for snapshot operations
  // Tool call fields
  toolName?: string;
  toolArgs?: Record<string, any>;
  toolResult?: any;
  toolSuccess?: boolean;
  toolCallId?: string; // anchors nested sub-agent events
  toolRationale?: string;
  toolGroupRationale?: string;
  subAgentEvents?: any[]; // nested events emitted by delegated sub-agents
}

const mapSessionMessage = (msg: any): Message | null => {
  const messageId = String(msg.id || `msg-${Date.now()}-${Math.random()}`);
  if (msg.role === 'TOOL_CALL' || msg.role === 'tool_call') {
    return {
      id: messageId,
      role: 'TOOL_CALL',
      content: '',
      timestamp: new Date(msg.created_at),
      toolName: msg.tool_name,
      toolArgs: msg.tool_input,
      toolResult: msg.tool_output,
      toolSuccess: msg.success,
      toolCallId: String(msg.tool_call_id || messageId),
      executionId: msg.execution ? String(msg.execution) : undefined,
      toolRationale: (msg.tool_rationale || msg.toolRationale || '').trim() || undefined,
      toolGroupRationale: (msg.tool_group_rationale || msg.toolGroupRationale || '').trim() || undefined,
      subAgentEvents: Array.isArray(msg.sub_agent_events)
        ? msg.sub_agent_events
        : Array.isArray(msg.subAgentEvents)
          ? msg.subAgentEvents
          : [],
    };
  }

  if (msg.role === 'ASSISTANT_STATUS' || msg.role === 'assistant_status') {
    return null;
  }

  const numericId = Number(msg.id);
  return {
    id: messageId,
    role: msg.role as 'USER' | 'ASSISTANT' | 'SYSTEM',
    content: msg.content,
    timestamp: new Date(msg.created_at),
    executionId: msg.execution,
    snapshotMode: msg.snapshot_mode,
    hasSnapshot: msg.has_snapshot,
    dbMessageId: Number.isNaN(numericId) ? undefined : numericId,
  };
};

const mapSessionMessages = (messages: any[] = []): Message[] =>
  messages.map(mapSessionMessage).filter((message): message is Message => Boolean(message));

const buildTerminalToolResult = (error: string, status: string) => ({
  success: false,
  data: status === 'ERROR'
    ? { error: true }
    : status === 'COMPLETED'
      ? { result_unavailable: true, execution_completed: true }
      : { cancelled: true },
  error,
});

const markPendingToolCallsTerminal = (
  messages: Message[],
  error: string,
  status: string = 'ERROR',
  executionId?: string,
): Message[] => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return messages;
  }

  const normalizedStatus = status === 'CANCELLED' || status === 'STOPPED'
    ? 'CANCELLED'
    : status === 'COMPLETED'
      ? 'COMPLETED'
      : 'ERROR';
  const terminalResult = buildTerminalToolResult(error, normalizedStatus);
  let changed = false;

  const next = messages.map((message) => {
    let updated = message;
    const matchesExecution = (
      !executionId ||
      !message.executionId ||
      String(message.executionId) === String(executionId)
    );

    if (!matchesExecution) {
      return message;
    }

    if (Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0) {
      const nested = markSubAgentEventsStopped(message.subAgentEvents, {
        errorMessage: error,
        status: normalizedStatus,
        data: terminalResult.data,
      });
      if (nested !== message.subAgentEvents) {
        updated = { ...updated, subAgentEvents: nested };
        changed = true;
      }
    }

    if (
      updated.role === 'TOOL_CALL' &&
      (updated.toolResult === undefined || updated.toolResult === null)
    ) {
      changed = true;
      return {
        ...updated,
        toolResult: terminalResult,
        toolSuccess: false,
      };
    }

    return updated;
  });

  return changed ? next : messages;
};

const getMessageKey = (message: Message) => `${message.role}:${message.id}`;

/**
 * ToolCallCard — Collapsible tool call display for AI Manager chat.
 * Shows tool name + status badge, expands on click to show arguments & result.
 */
const ToolCallCard: React.FC<{ message: Message; isDark: boolean }> = ({ message, isDark }) => {
  const [expanded, setExpanded] = useState(false);

  const isRunning = message.toolResult === undefined || message.toolResult === null;
  const isCancelled = message.toolResult?.data?.cancelled === true;
  const isResultUnavailable = message.toolResult?.data?.result_unavailable === true;
  const isSuccess = message.toolSuccess === true;
  const statusColor = isRunning
    ? 'warning.main'
    : isCancelled
      ? 'text.secondary'
      : isResultUnavailable
        ? 'warning.main'
        : isSuccess
          ? 'success.main'
          : 'error.main';
  const statusLabel = isRunning
    ? 'Running...'
    : isCancelled
      ? 'Stopped'
      : isResultUnavailable
        ? 'Result unavailable'
        : isSuccess
          ? 'Completed'
          : 'Failed';
  const statusIcon = isRunning
    ? '⏳'
    : isCancelled
      ? '■'
      : isResultUnavailable
        ? '!'
        : isSuccess
          ? '✓'
          : '✗';

  return (
    <Box sx={{ mb: 1, ml: 5 }}>
      <Paper
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark
            ? 'rgba(33, 150, 243, 0.06)'
            : 'rgba(33, 150, 243, 0.04)',
          transition: 'all 0.15s ease',
          overflow: 'hidden',
          '&:hover': {
            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header — always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              transition: 'transform 0.15s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              lineHeight: 1,
            }}
          >
            ▶
          </Typography>
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: '0.8rem' }}
            >
              {message.toolName}
            </Typography>
            <ToolCallReason reason={message.toolRationale} />
          </Box>
          <Chip
            label={`${statusIcon} ${statusLabel}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 500,
              bgcolor: `color-mix(in srgb, ${isDark ? '#fff' : '#000'} 8%, transparent)`,
              color: statusColor,
              border: '1px solid',
              borderColor: statusColor,
            }}
          />
        </Box>

        {/* Expanded content */}
        {expanded && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              px: 1.5,
              py: 1,
            }}
          >
            {/* Arguments */}
            {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
              <Box sx={{ mb: message.toolResult ? 1.5 : 0 }}>
                <Typography
                  variant="overline"
                  sx={{ fontSize: '0.6rem', color: 'text.secondary', letterSpacing: '0.05em' }}
                >
                  Arguments
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 150,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {JSON.stringify(message.toolArgs, null, 2)}
                </Box>
              </Box>
            )}

            {/* Result */}
            {message.toolResult && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontSize: '0.6rem', color: 'text.secondary', letterSpacing: '0.05em' }}
                >
                  Result
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 200,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {JSON.stringify(message.toolResult, null, 2)}
                </Box>
              </Box>
            )}

            {/* Running indicator */}
            {isRunning && (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                Waiting for result...
              </Typography>
            )}

            {/* Nested sub-agent events */}
            {Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0 && (
              <SubAgentEventsTree events={message.subAgentEvents} />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/**
 * ToolCallGroup — Collapsible container grouping consecutive tool calls.
 * Shows a summary header "🔧 N tool calls" that toggles the list.
 */
const ToolCallGroup: React.FC<{ toolMsgs: Message[]; isDark: boolean }> = ({ toolMsgs, isDark }) => {
  const [collapsed, setCollapsed] = useState(true);

  const allDone = toolMsgs.every(m => m.toolResult !== undefined && m.toolResult !== null);
  const headerColor = allDone ? 'success.main' : 'warning.main';
  const headerIcon = allDone ? '✓' : '⏳';
  const headerLabel = allDone ? 'Done' : 'Running...';
  const toolNames = toolMsgs.map(m => m.toolName).join(', ');
  const groupRationale = toolMsgs.find(m => m.toolGroupRationale)?.toolGroupRationale
    || (toolMsgs.length === 1 ? toolMsgs[0].toolRationale : undefined);

  return (
    <Box sx={{ mb: 1, ml: 5 }}>
      <Paper
        sx={{
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark ? 'rgba(33, 150, 243, 0.04)' : 'rgba(33, 150, 243, 0.03)',
          overflow: 'hidden',
        }}
      >
        {/* Group header */}
        <Box
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              transition: 'transform 0.15s ease',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              lineHeight: 1,
            }}
          >
            ▶
          </Typography>
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            {toolMsgs.length} tool call{toolMsgs.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {toolNames}
          </Typography>
          <Chip
            label={`${headerIcon} ${headerLabel}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 500,
              bgcolor: `color-mix(in srgb, ${isDark ? '#fff' : '#000'} 8%, transparent)`,
              color: headerColor,
              border: '1px solid',
              borderColor: headerColor,
            }}
          />
        </Box>

        {groupRationale && (
          <Box
            sx={{
              px: 1.5,
              pb: 0.75,
              mt: -0.25,
              color: 'text.secondary',
              fontSize: '0.78rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {groupRationale}
          </Box>
        )}

        {/* Collapsible tool call list */}
        {!collapsed && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 0.75,
            }}
          >
            {toolMsgs.map(msg => (
              <ToolCallCard key={msg.id} message={msg} isDark={isDark} />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/**
 * Groups consecutive TOOL_CALL messages together.
 * Returns an array of items: regular Message objects or { type: 'tool_group', id, msgs }.
 */
function groupMessagesForAIBuilder(messages: Message[]): Array<Message | { type: 'tool_group'; id: string; msgs: Message[] }> {
  const result: Array<Message | { type: 'tool_group'; id: string; msgs: Message[] }> = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'TOOL_CALL') {
      const group: Message[] = [];
      while (i < messages.length && messages[i].role === 'TOOL_CALL') {
        group.push(messages[i]);
        i++;
      }
      result.push({ type: 'tool_group', id: `tg-${group[0].id}`, msgs: group });
    } else {
      result.push(messages[i]);
      i++;
    }
  }
  return result;
}

const AIBuilder: React.FC<AIBuilderProps> = ({ 
  system, 
  agent, 
  onExpertCreated,
  headerActions,
  chatOnly = false,
  chatTitle = 'AI Manager',
  assistantLabel = 'AI Manager',
  emptyStateTitle = 'Start a conversation to build your AI system',
  emptyStateHint = 'Try: "Add an expert for technical analysis" or "Configure the system settings"',
  inputPlaceholder = 'Ask AI Manager to help configure your system...',
  initialMessagePrefix,
  executionSystemId,
  executionContext,
  sessionAiSystemId,
  sessionQueryParams,
  autoLoadLatestSession = true,
  enableSessionHistory = true,
  enableSnapshots = true,
  refreshToolNames,
  // Canvas props
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  setSelectedNode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues,
  onSaveCanvas,
  isSavingCanvas,
  onRunCanvas,
  onStopCanvas,
  isRunningCanvas,
  isStoppingCanvas,
  canRunCanvas,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  nodeTypes,
  onExpertUpdate,
}) => {
  const queryClient = useQueryClient();
  const systemBuilderAccess = useActionAccess(AI_ACTION_SURFACES.systemBuilderSend);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileTab, setMobileTab] = useState(0); // 0 = Chat, 1 = Workflow
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isStartingExecution, setIsStartingExecution] = useState(false);
  const [selectedMessageIndex, setSelectedMessageIndex] = useState<number | null>(null); // Track which message's execution to view
  const [sessionId, setSessionId] = useState<string | null>(null); // Track conversation session
  const [currentSessionData, setCurrentSessionData] = useState<any>(null); // Track loaded session with snapshot
  const [snapshotView, setSnapshotView] = useState<{ messageId: string; snapshot: any } | null>(null);
  const [snapshotLoadingMessageId, setSnapshotLoadingMessageId] = useState<string | null>(null);
  const [isHistoricalSession, setIsHistoricalSession] = useState(false); // Track if viewing historical session
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false); // Track when loading a session
  const [sessionsPage, setSessionsPage] = useState(1); // Pagination for sessions
  const sessionsPageSize = 10; // Show 10 sessions per page
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextBeforeMessageId, setNextBeforeMessageId] = useState<number | null>(null);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const userStartedNewConversation = useRef(false); // Track if user explicitly clicked "New"
  const sessionIdRef = useRef<string | null>(null); // Ref to track sessionId for callbacks
  const isLoadingOlderMessagesRef = useRef(false);
  const isPrependingMessagesRef = useRef(false);
  const pendingScrollRestoreRef = useRef<{ height: number; top: number } | null>(null);
  const shouldScrollToBottomRef = useRef(true);
  const suppressNextVoiceMessageRef = useRef(false);
  const completedExecutionMessagesRef = useRef<Set<string>>(new Set());
  const pendingVoiceResponseRef = useRef(false);
  const voiceResponseBaselineRef = useRef<string | null>(null);
  const spokenVoiceExecutionIdsRef = useRef<Set<string>>(new Set());
  // Buffer for sub-agent events that arrive before their parent TOOL_CALL
  // message has been committed to React state. Flushed on every state
  // update that introduces a new TOOL_CALL message.
  const pendingSubAgentEventsRef = useRef<Record<string, any[]>>({});
  const pendingToolResultsRef = useRef<Record<string, any[]>>({});
  const pendingToolRationaleRef = useRef<string | null>(null);

  const mergePersistedToolMessages = useCallback((persistedMessages: any[]) => {
    setMessages((prev) => (
      mergePersistedToolResults(prev as any[], persistedMessages) as typeof prev
    ));
  }, []);

  const reconcileExecutionToolMessages = useCallback(async (executionId: string) => {
    try {
      const persistedMessages = await agentApi.getExecutionMessages(
        executionId,
        { compactToolOutputs: true },
      );
      mergePersistedToolMessages(persistedMessages);
    } catch (error) {
      console.warn('[AIBuilder] Failed to reconcile persisted tool messages:', error);
    }
  }, [mergePersistedToolMessages]);

  const effectiveExecutionSystemId = executionSystemId === undefined ? system.id : executionSystemId;
  const effectiveSessionAiSystemId = sessionAiSystemId === undefined ? system.id : sessionAiSystemId;
  const normalizedSessionQueryParams = useMemo(
    () => Object.fromEntries(
      Object.entries(sessionQueryParams || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .sort(([left], [right]) => left.localeCompare(right))
    ),
    [sessionQueryParams],
  );
  const serializedSessionQueryParams = useMemo(
    () => JSON.stringify(normalizedSessionQueryParams),
    [normalizedSessionQueryParams],
  );
  const sessionScopeKey = effectiveSessionAiSystemId
    ?? (serializedSessionQueryParams !== '{}'
      ? `agent-${agent.id}-${serializedSessionQueryParams}`
      : `agent-${agent.id}`);
  const defaultMessagePrefix = initialMessagePrefix ?? `AI System (name: ${system.name}, ID: ${system.id})`;
  const dataModifyingToolNames = useMemo(
    () => new Set([
      ...DEFAULT_DATA_MODIFYING_TOOL_NAMES,
      ...(refreshToolNames || []),
    ]),
    [refreshToolNames],
  );

  const refreshAfterDataModifyingTool = useCallback((
    toolName?: string | null,
    result?: any,
    source: string = 'tool',
  ) => {
    if (!toolName || result?.success === false || !dataModifyingToolNames.has(toolName)) {
      return;
    }

    console.log(`[AIBuilder] ${source} ${toolName} completed, refreshing system data`);

    if (onExpertCreated) {
      Promise.resolve(onExpertCreated()).catch((refreshError) => {
        console.warn('[AIBuilder] Failed to refresh after tool result:', refreshError);
      });
    }
    if (refetchExperts) {
      Promise.resolve(refetchExperts()).catch((refreshError) => {
        console.warn('[AIBuilder] Failed to refresh canvas experts after tool result:', refreshError);
      });
    }

    if (effectiveExecutionSystemId) {
      queryClient.invalidateQueries({
        queryKey: ['ai-system', String(effectiveExecutionSystemId)]
      });
      queryClient.invalidateQueries({
        queryKey: ['experts', effectiveExecutionSystemId]
      });
      queryClient.invalidateQueries({
        queryKey: ['schedules', effectiveExecutionSystemId]
      });
    }
  }, [
    dataModifyingToolNames,
    effectiveExecutionSystemId,
    onExpertCreated,
    queryClient,
    refetchExperts,
  ]);

  // Keep ref in sync
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const shouldAutoLoadLatestSession = autoLoadLatestSession
    && !sessionId
    && messages.length === 0
    && !userStartedNewConversation.current;

  // File attachment state
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Restore from snapshot state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState<Message | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreSnackbar, setRestoreSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(35); // Chat panel percentage
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle resize drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Clamp between 20% and 80%
      const clampedWidth = Math.min(Math.max(newWidth, 20), 80);
      setLeftPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  // WebSocket-based execution hook (uses AgentChatConsumer for streaming)
  const {
    isConnected: wsConnected,
    isExecuting,
    currentExecutionId: wsExecutionId,
    currentQueuedTaskId,
    currentSessionId: wsSessionId,
    executionStatus,
    progress,
    currentIteration,
    maxIterations,
    expertProgress,
    error: wsError,
    totalCost,
    totalTokens,
    toolNames,
    executionResult,
    executeWithMessage,
    stopExecution,
    subscribeToExecution,
    reset: resetExecution,
    connect: connectWs,
    disconnect: disconnectWs,
    pendingConfirmation,
    confirmTool,
  } = useAIBuilderExecution({
    systemId: effectiveExecutionSystemId,
    agentId: agent.id,
    executionContext,
    autoConnect: true,
    onToolCall: (toolName, toolArgs, toolCallId, data) => {
      // Add tool call message to chat
      setMessages((prev) => {
        const toolRationale = (data?.tool_rationale || data?.toolRationale || '').trim()
          || undefined;
        const toolGroupRationale = (data?.tool_group_rationale || data?.toolGroupRationale || '').trim()
          || pendingToolRationaleRef.current
          || undefined;
        pendingToolRationaleRef.current = null;
        const next: Message[] = [
          ...prev,
          {
            id: toolCallId
              ? `tool-${toolCallId}`
              : `tool-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${toolName}`,
            role: 'TOOL_CALL',
            content: '',
            timestamp: new Date(),
            toolName,
            toolArgs,
            toolCallId,
            executionId: data?.execution_id || data?.executionId || undefined,
            toolRationale,
            toolGroupRationale,
            subAgentEvents: [],
          },
        ];
        // Flush any events/results that arrived before this parent committed.
        const withSubAgents = flushPendingSubAgentEvents(
          next as any[],
          pendingSubAgentEventsRef.current,
        );
        return flushPendingToolResults(
          withSubAgents,
          pendingToolResultsRef.current,
        ) as typeof next;
      });
    },
    onToolResult: (toolName, result, toolCallId) => {
      // Reconcile by tool_call_id first; buffer until the card exists.
      setMessages((prev) => {
        const applied = applyToolResultToMessages(prev as any[], {
          toolName,
          toolCallId,
          result,
        });
        if (!applied.applied) {
          bufferPendingToolResult(pendingToolResultsRef.current, {
            toolName,
            toolCallId,
            result,
          });
          console.warn(
            '[AIBuilder] Buffered tool result (tool_call not committed yet)',
            { toolName, toolCallId },
          );
          return prev;
        }
        return applied.messages as typeof prev;
      });

      refreshAfterDataModifyingTool(toolName, result);
    },
    onAssistantStatus: (content, _iteration, data) => {
      const trimmedContent = (content || '').trim();
      if (!trimmedContent) return;
      pendingToolRationaleRef.current = trimmedContent;
    },
    onSubAgentEvent: (event) => {
      // Nested event produced by a delegated sub-agent. Attach it to the
      // matching parent TOOL_CALL message so the UI renders it nested.
      // If the parent is not in state yet (race with tool_call event),
      // buffer the event and let onToolCall flush it when the parent
      // gets committed.
      setMessages((prev) => {
        const updated = attachSubAgentEvent(prev as any[], event) as typeof prev;
        if (updated === (prev as any)) {
          const parentId = event.parent_tool_call_id;
          const buf = pendingSubAgentEventsRef.current;
          if (!buf[parentId]) buf[parentId] = [];
          buf[parentId].push(event);
          console.warn(
            '[AIBuilder] Buffered sub-agent event (parent not committed yet)',
            { parentId, type: event.type, buffered: buf[parentId].length },
          );
          return prev;
        }
        return flushPendingSubAgentEvents(
          updated as any[],
          pendingSubAgentEventsRef.current,
        ) as typeof prev;
      });

      if (event?.type === 'tool_result') {
        refreshAfterDataModifyingTool(
          event.tool_name || event.toolName,
          event.result,
          `sub-agent ${event.sub_agent_name || 'tool'}`
        );
      }
    },
    onExecutionCompleted: async (execId, status) => {
      console.log(`[AIBuilder] Execution ${execId} completed with status: ${status}`);
      if (!execId) {
        console.warn('[AIBuilder] Ignoring completion event without execution id');
        return;
      }

      if (completedExecutionMessagesRef.current.has(execId)) {
        console.log('[AIBuilder] Completion message already rendered for:', execId);
        return;
      }
      completedExecutionMessagesRef.current.add(execId);
      
      // Fetch lightweight execution state to get the response without step/tool payloads
      try {
        const executionSummary = await agentApi.getExecutionSummary(execId);

        const currentSessionId = sessionIdRef.current;
        if (
          (currentSessionId && executionSummary.session_id && executionSummary.session_id !== currentSessionId) ||
          (!currentSessionId && userStartedNewConversation.current)
        ) {
          console.log('[AIBuilder] Ignoring completion for inactive conversation:', {
            executionId: execId,
            executionSessionId: executionSummary.session_id,
            currentSessionId,
          });
          return;
        }

        await reconcileExecutionToolMessages(execId);
        
        // Determine message content based on status
        let messageContent = '';
        if (status === 'CANCELLED' || status === 'STOPPED') {
          messageContent = '⚠️ Execution was stopped by user.';
        } else if (status === 'ERROR') {
          messageContent = `❌ Execution failed: ${executionSummary.error || 'Unknown error'}`;
        } else {
          messageContent = executionSummary.result?.response || 'Execution completed';
        }

        // Add assistant response
        setMessages((prev) => {
          const visibleMessages = prev.filter(m => !m.id.startsWith('assistant-stopping-'));
          const filtered = (
            status === 'CANCELLED' || status === 'STOPPED' || status === 'ERROR'
          )
            ? markPendingToolCallsTerminal(
                visibleMessages,
                status === 'ERROR'
                  ? (executionSummary.error || 'Execution failed before the tool returned.')
                  : 'Execution cancelled by user.',
                status,
              )
            : visibleMessages;
          const newMessages: Message[] = [
            ...filtered,
            {
              id: `assistant-${Date.now()}`,
              role: 'ASSISTANT' as const,
              content: messageContent,
              timestamp: new Date(),
              executionId: execId,
              voiceReady: status === 'COMPLETED',
            },
          ];
          setSelectedMessageIndex(newMessages.length - 1);
          return newMessages;
        });

        // Trigger data refresh only for successful completions
        if (status === 'COMPLETED') {
          if (onExpertCreated) {
            console.log('🔄 Triggering data refresh callback...');
            await onExpertCreated();
          }
          if (refetchExperts) {
            await refetchExperts();
          }
          
          if (effectiveExecutionSystemId) {
            queryClient.invalidateQueries({ 
              queryKey: ['ai-system', String(effectiveExecutionSystemId)]
            });
            queryClient.invalidateQueries({ 
              queryKey: ['ai-systems']
            });
          }
        }
        
        // Always invalidate and refetch sessions after execution completes (to update history)
        setSessionsPage(1); // Reset to first page to see latest conversation
        queryClient.invalidateQueries({ 
          queryKey: ['conversation-sessions', agent.id, sessionScopeKey]
        });
        queryClient.invalidateQueries({
          queryKey: ['latest-conversation-session', agent.id, sessionScopeKey]
        });

        // Refresh messages from the API to get real DB IDs and snapshot data
        const currentSid = sessionIdRef.current;
        if (currentSid) {
          try {
            const sessionData = await agentApi.getSession(
              currentSid,
              buildCompactSessionPageRequest(),
            );
            const refreshedMessages = mapSessionMessages(sessionData.messages);
            setCurrentSessionData(sessionData);
            setMessages((liveMessages) => {
              const reconciledMessages = reconcileCompletionSessionMessages(
                liveMessages,
                refreshedMessages,
                execId,
              ) as Message[];
              let lastAssistantIdx = -1;
              for (let i = reconciledMessages.length - 1; i >= 0; i--) {
                if (reconciledMessages[i].role === 'ASSISTANT') {
                  lastAssistantIdx = i;
                  break;
                }
              }
              if (lastAssistantIdx >= 0) {
                setSelectedMessageIndex(lastAssistantIdx);
              }
              return reconciledMessages;
            });
            setHasMoreMessages(Boolean(sessionData.messages_page?.has_more));
            setNextBeforeMessageId(sessionData.messages_page?.next_before_message_id ?? null);
          } catch (refreshError) {
            console.warn('Failed to refresh messages from API:', refreshError);
          }
        }

        if (status === 'COMPLETED') {
          setMessages((currentMessages) => markPendingToolCallsTerminal(
            currentMessages,
            (
              'Execution completed, but the tool result was not available from '
              + 'the live stream or persisted history.'
            ),
            'COMPLETED',
            execId,
          ));
        }
      } catch (error) {
        console.error('Failed to fetch execution details:', error);
        if (status === 'COMPLETED') {
          setMessages((currentMessages) => markPendingToolCallsTerminal(
            currentMessages,
            'Execution completed, but its tool result could not be recovered.',
            'COMPLETED',
            execId,
          ));
        }
      }
    },
    onError: (error) => {
      console.error('[AIBuilder] Execution error:', error);
      setMessages((prev) => {
        const closedPendingTools = markPendingToolCallsTerminal(prev, error, 'ERROR');

        return [
          ...closedPendingTools,
          {
            id: `error-${Date.now()}`,
            role: 'ASSISTANT',
            content: `Error: ${error}`,
            timestamp: new Date(),
          },
        ];
      });
    },
  });

  const rememberActiveSession = useCallback((nextSessionId?: string | null) => {
    if (!nextSessionId || sessionIdRef.current === nextSessionId) {
      return;
    }

    sessionIdRef.current = nextSessionId;
    setSessionId(nextSessionId);
    setIsHistoricalSession(false);
    queryClient.invalidateQueries({ queryKey: ['conversation-sessions', agent.id, sessionScopeKey] });
    queryClient.invalidateQueries({ queryKey: ['latest-conversation-session', agent.id, sessionScopeKey] });
  }, [agent.id, queryClient, sessionScopeKey]);

  useEffect(() => {
    if (!wsSessionId) return;
    if (userStartedNewConversation.current && messages.length === 0) return;

    rememberActiveSession(wsSessionId);
  }, [messages.length, rememberActiveSession, wsSessionId]);

  const currentExecutionId = wsExecutionId;
  const isExecutionQueued = executionStatus === 'QUEUED';
  const isChatBusy = isExecuting || isStartingExecution || isExecutionQueued;

  useEffect(() => {
    if (!isExecuting || !currentExecutionId || !hasPendingToolCallMessages(messages)) {
      return;
    }

    let cancelled = false;
    const refreshToolMessages = async () => {
      if (cancelled) return;
      await reconcileExecutionToolMessages(currentExecutionId);
    };

    refreshToolMessages();
    const interval = window.setInterval(refreshToolMessages, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentExecutionId, isExecuting, messages, reconcileExecutionToolMessages]);

  const handleVoiceRecording = useCallback(async (file: File) => {
    if (isChatBusy) return;
    if (!systemBuilderAccess.ensureAccess()) return;
    if (!canAgentAcceptAudio(agent)) {
      throw new Error(getAgentAudioInputDisabledReason(agent));
    }

    try {
      const lastAssistant = [...messages].reverse().find((m) => m.role === 'ASSISTANT' && m.content);
      pendingVoiceResponseRef.current = true;
      voiceResponseBaselineRef.current = lastAssistant?.id || null;
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const visibleContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      let messageContent = visibleContent;
      if (messages.length === 0 && defaultMessagePrefix) {
        messageContent = `${defaultMessagePrefix}\n\n${messageContent}`;
      }

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'USER',
        content: visibleContent,
        timestamp: new Date(),
        hasSnapshot: enableSnapshots,
        snapshotMode: enableSnapshots ? 'FULL' : 'NONE',
      };
      shouldScrollToBottomRef.current = true;
      setMessages((prev) => [...prev, userMessage]);
      setSelectedMessageIndex(null);
      const activeSessionId = sessionIdRef.current || wsSessionId || sessionId;
      const response = await executeWithMessage(messageContent, activeSessionId, {
        system_name: system.name,
        attachment_ids: [attachment.id],
      });
      rememberActiveSession(response.session_id);
      setInputMessage('');
    } catch (error) {
      console.error('Voice execution failed:', error);
      pendingVoiceResponseRef.current = false;
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  }, [agent, defaultMessagePrefix, enableSnapshots, executeWithMessage, isChatBusy, messages, rememberActiveSession, sessionId, system.name, systemBuilderAccess, wsSessionId]);

  // Voice chat — record and send audio to the LLM
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    ttsVoice, setTtsVoice,
    voiceRate, setVoiceRate,
    isRecordingSupported, isTTSSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
    onSpeakResponse: canAgentSpeakAudio(agent)
      ? async (text, options) => {
          if (!options.executionId) {
            throw new Error('Voice output requires a completed execution.');
          }
          return agentApi.synthesizeSpeech(options.executionId, {
            text,
            voice: options.voice,
            speed: options.speed,
          });
        }
      : undefined,
  });
  const audioInputSupported = canAgentAcceptAudio(agent);
  const audioInputDisabledReason = getAgentAudioInputDisabledReason(agent);
  const voiceButtonDisabled = systemBuilderAccess.disabled || !audioInputSupported;
  const voiceButtonDisabledReason = systemBuilderAccess.disabled
    ? systemBuilderAccess.blockedMessage
    : audioInputDisabledReason;

  // Speak the completed assistant response for the active voice turn.
  useEffect(() => {
    if (suppressNextVoiceMessageRef.current) {
      suppressNextVoiceMessageRef.current = false;
      return;
    }

    if (!isVoiceOn || !pendingVoiceResponseRef.current) return;

    const lastMsg = messages.length > 0
      ? [...messages].reverse().find((m) => m.role === 'ASSISTANT' && m.content)
      : undefined;
    const baselineId = voiceResponseBaselineRef.current;
    const isNewVoiceResponse = Boolean(lastMsg && lastMsg.id !== baselineId);

    if (lastMsg && isNewVoiceResponse && lastMsg.voiceReady && lastMsg.executionId) {
      if (!spokenVoiceExecutionIdsRef.current.has(lastMsg.executionId)) {
        spokenVoiceExecutionIdsRef.current.add(lastMsg.executionId);
        pendingVoiceResponseRef.current = false;
        speakResponse(stripMarkdown(lastMsg.content), lastMsg.executionId);
      }
      return;
    }

    if (!isExecuting && voiceState === 'processing' && isNewVoiceResponse && !lastMsg?.executionId) {
      pendingVoiceResponseRef.current = false;
      stopVoice();
    }
  }, [isExecuting, isVoiceOn, messages, speakResponse, stopVoice, voiceState]);

  // Auto-scroll to bottom when live messages arrive.
  const isNearMessagesBottom = useCallback((container: HTMLDivElement | null = messagesContainerRef.current) => {
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= AUTO_SCROLL_BOTTOM_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useLayoutEffect(() => {
    const pending = pendingScrollRestoreRef.current;
    const container = messagesContainerRef.current;
    if (!pending || !container) return;

    pendingScrollRestoreRef.current = null;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight - pending.height + pending.top;
      shouldScrollToBottomRef.current = isNearMessagesBottom(container);
      isPrependingMessagesRef.current = false;
    });
  }, [messages, isNearMessagesBottom]);

  useEffect(() => {
    if (isPrependingMessagesRef.current || !shouldScrollToBottomRef.current) {
      return;
    }
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const applySessionData = useCallback(async (sessionData: ConversationSession) => {
    userStartedNewConversation.current = false;
    setSnapshotView(null);

    // Store full session data including snapshot
    setCurrentSessionData(sessionData);

    // Load messages from the current backend page with execution IDs and snapshot info.
    const loadedMessages = mapSessionMessages(sessionData.messages);
    pendingSubAgentEventsRef.current = {};
    clearPendingToolResults(pendingToolResultsRef.current);
    pendingToolRationaleRef.current = null;

    console.log('[AIBuilder] Loaded session messages with snapshot info:', 
      loadedMessages.map(m => ({ id: m.id, role: m.role, hasSnapshot: m.hasSnapshot, snapshotMode: m.snapshotMode }))
    );

    setMessages(loadedMessages);
    setHasMoreMessages(Boolean(sessionData.messages_page?.has_more));
    setNextBeforeMessageId(sessionData.messages_page?.next_before_message_id ?? null);
    shouldScrollToBottomRef.current = true;
    setSessionId(sessionData.id);
    setShowHistory(false);

    const runningExecution = sessionData.executions?.find(
      (exec: any) => exec.status === 'RUNNING'
    );

    if (runningExecution) {
      console.log('🔄 Found running execution, subscribing via WebSocket:', runningExecution.id);

      setSelectedMessageIndex(null);

      await subscribeToExecution(runningExecution.id);
      setIsHistoricalSession(false);
      return;
    }

    setIsHistoricalSession(true);

    let lastAssistantMessageIndex = -1;
    for (let i = loadedMessages.length - 1; i >= 0; i--) {
      if (loadedMessages[i].role === 'ASSISTANT' && loadedMessages[i].executionId) {
        lastAssistantMessageIndex = i;
        break;
      }
    }

    if (lastAssistantMessageIndex !== -1) {
      setSelectedMessageIndex(lastAssistantMessageIndex);
    } else if (sessionData.executions && sessionData.executions.length > 0) {
      setSelectedMessageIndex(null);
    } else {
      setSelectedMessageIndex(null);
    }
  }, [subscribeToExecution]);

  const loadOlderMessages = useCallback(async () => {
    const currentSessionId = sessionIdRef.current;
    const container = messagesContainerRef.current;
    if (
      !currentSessionId ||
      !nextBeforeMessageId ||
      !hasMoreMessages ||
      isLoadingOlderMessages ||
      isLoadingOlderMessagesRef.current ||
      !container
    ) {
      return;
    }

    isLoadingOlderMessagesRef.current = true;
    pendingScrollRestoreRef.current = {
      height: container.scrollHeight,
      top: container.scrollTop,
    };
    isPrependingMessagesRef.current = true;
    suppressNextVoiceMessageRef.current = true;
    shouldScrollToBottomRef.current = false;
    setIsLoadingOlderMessages(true);

    try {
      const sessionData = await agentApi.getSession(
        currentSessionId,
        buildCompactSessionPageRequest({ beforeMessageId: nextBeforeMessageId }),
      );
      const olderMessages = mapSessionMessages(sessionData.messages);

      setMessages((prev) => {
        const existingKeys = new Set(prev.map(getMessageKey));
        const newMessages = olderMessages.filter((message) => !existingKeys.has(getMessageKey(message)));
        return [...newMessages, ...prev];
      });
      setHasMoreMessages(Boolean(sessionData.messages_page?.has_more));
      setNextBeforeMessageId(sessionData.messages_page?.next_before_message_id ?? null);
      setCurrentSessionData((prev: any) => {
        if (!prev || prev.id !== sessionData.id) return prev;
        const existingIds = new Set((prev.messages || []).map((msg: any) => `${msg.role}:${msg.id}`));
        const newRawMessages = (sessionData.messages || []).filter(
          (msg: any) => !existingIds.has(`${msg.role}:${msg.id}`)
        );
        return {
          ...prev,
          messages: [...newRawMessages, ...(prev.messages || [])],
          messages_page: sessionData.messages_page,
        };
      });
    } catch (error) {
      console.error('Failed to load older messages:', error);
      pendingScrollRestoreRef.current = null;
      isPrependingMessagesRef.current = false;
      suppressNextVoiceMessageRef.current = false;
      toast.error('Could not load earlier messages');
    } finally {
      isLoadingOlderMessagesRef.current = false;
      setIsLoadingOlderMessages(false);
    }
  }, [hasMoreMessages, isLoadingOlderMessages, nextBeforeMessageId]);

  const handleMessagesScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const nearBottom = isNearMessagesBottom(event.currentTarget);
    shouldScrollToBottomRef.current = nearBottom;
    if (event.currentTarget.scrollTop <= 24 && !nearBottom) {
      void loadOlderMessages();
    }
  }, [isNearMessagesBottom, loadOlderMessages]);

  const {
    data: latestSessionData,
    isLoading: isLoadingLatestSession,
  } = useQuery({
    queryKey: ['latest-conversation-session', agent.id, sessionScopeKey],
    queryFn: async () => agentApi.getLatestSession(
      agent.id,
      true,
      effectiveSessionAiSystemId ?? undefined,
      normalizedSessionQueryParams,
      buildCompactSessionPageRequest(),
    ),
    enabled: Boolean(agent.id) && shouldAutoLoadLatestSession,
    staleTime: 30_000,
    retry: false,
  });

  // Fetch conversation sessions for this agent and AI system
  const { data: sessionsData, isLoading: isLoadingSessions, isError: isSessionsError } = useQuery({
    queryKey: ['conversation-sessions', agent.id, sessionScopeKey, sessionsPage],
    queryFn: async () => {
      // Pass AI system ID to API to filter server-side, ordered by last_message_at DESC
      const response = await agentApi.getSessions(
        agent.id,
        true,
        sessionsPage,
        sessionsPageSize,
        effectiveSessionAiSystemId ?? undefined,
        normalizedSessionQueryParams,
      );
      return response;
    },
    enabled: Boolean(agent.id && showHistory && enableSessionHistory),
    staleTime: 30_000, // Cache for 30s to avoid re-fetching on every mount
    retry: false, // Don't retry on 404 errors
  });

  const groupedMessages = useMemo(() => groupMessagesForAIBuilder(messages), [messages]);

  // Reset to page 1 if we get an error (e.g., page out of range)
  useEffect(() => {
    if (isSessionsError && sessionsPage > 1) {
      setSessionsPage(1);
    }
  }, [isSessionsError, sessionsPage]);

  // Reset to previous page if current page is empty but there are sessions
  useEffect(() => {
    if (sessionsData && sessionsData.results.length === 0 && sessionsData.count > 0 && sessionsPage > 1) {
      // Current page is empty, go to previous page
      setSessionsPage(sessionsPage - 1);
    }
  }, [sessionsData, sessionsPage]);

  // Auto-load the latest session detail without waiting on history pagination.
  useEffect(() => {
    if (!latestSessionData || !shouldAutoLoadLatestSession) {
      return;
    }

    let shouldLogErrors = true;

    const loadLatestSession = async () => {
      setIsLoadingSession(true);
      try {
        await applySessionData(latestSessionData);
      } catch (error) {
        if (shouldLogErrors) {
          console.error('Failed to load latest conversation:', error);
        }
      } finally {
        setIsLoadingSession(false);
      }
    };

    void loadLatestSession();

    return () => {
      shouldLogErrors = false;
    };
  }, [applySessionData, latestSessionData, shouldAutoLoadLatestSession]);

  // Handle stop execution via WebSocket hook
  const handleStopExecution = async () => {
    if (currentExecutionId || currentQueuedTaskId) {
      const stoppingMessageId = `assistant-stopping-${Date.now()}`;
      // Add immediate feedback
      shouldScrollToBottomRef.current = true;
      setMessages((prev) => [
        ...prev,
        {
          id: stoppingMessageId,
          role: 'ASSISTANT' as const,
          content: currentExecutionId ? 'Stopping execution...' : 'Cancelling queued execution...',
          timestamp: new Date(),
        },
      ]);
      await stopExecution();
      if (!currentExecutionId && currentQueuedTaskId) {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === stoppingMessageId
              ? { ...message, content: 'Queued execution cancelled.' }
              : message
          )
        );
      }
    }
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!systemBuilderAccess.ensureAccess()) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        console.log('[AIBuilder] Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
        const attachment = await agentApi.uploadAttachment(file);
        console.log('[AIBuilder] Upload response - ID:', attachment.id, 'name:', attachment.original_name);
        return attachment;
      });

      const uploadedAttachments = await Promise.all(uploadPromises);
      setPendingAttachments((prev) => {
        const newList = [...prev, ...uploadedAttachments];
        console.log('[AIBuilder] Updated pendingAttachments:', newList.map(a => ({ id: a.id, name: a.original_name })));
        return newList;
      });
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setIsUploading(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove a pending attachment
  const handleRemoveAttachment = async (attachmentId: string) => {
    try {
      await agentApi.deleteAttachment(attachmentId);
      setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (error) {
      console.error('Failed to remove attachment:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && pendingAttachments.length === 0) || isChatBusy) return;
    if (!systemBuilderAccess.ensureAccess()) return;

    userStartedNewConversation.current = false;
  const draftInputMessage = inputMessage;
  const draftPendingAttachments = pendingAttachments;

    // Prepare message content - keep system info hidden from the visible chat bubble.
    let visibleContent = inputMessage;
    let messageContent = inputMessage;
    if (messages.length === 0 && defaultMessagePrefix) {
      messageContent = `${defaultMessagePrefix}\n\n${inputMessage}`;
    }

    // Add file information to the message if there are attachments
    if (pendingAttachments.length > 0) {
      const fileList = pendingAttachments
        .map((a) => `- ${a.original_name} (${formatFileSize(a.size)}, ${a.attachment_type})${a.file_url ? ` [View](${a.file_url})` : ''}`)
        .join('\n');
      visibleContent = visibleContent
        ? `${visibleContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
      messageContent = messageContent 
        ? `${messageContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
    }

    // Add user message with snapshot info (all new messages get snapshots)
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'USER',
      content: visibleContent,
      timestamp: new Date(),
      hasSnapshot: enableSnapshots,
      snapshotMode: enableSnapshots ? 'FULL' : 'NONE',
    };
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    
    // Clear selection for new execution
    setSelectedMessageIndex(null);
    
    // Clear pending attachments after including them in the message
    const attachmentIds = pendingAttachments.map((a) => a.id);
    setPendingAttachments([]);
    
    // Execute agent via WebSocket-enabled hook
    try {
      setIsStartingExecution(true);
      const activeSessionId = sessionIdRef.current || wsSessionId || sessionId;
      const response = await executeWithMessage(messageContent, activeSessionId, {
        system_name: system.name,
        attachment_ids: attachmentIds,
      });
      rememberActiveSession(response.session_id);
    } catch (error) {
      console.error('Execution failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      if (/websocket|not connected|failed to send/i.test(errorMessage)) {
        setInputMessage(draftInputMessage);
        setPendingAttachments(draftPendingAttachments);
      }
      toast.error(errorMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'ASSISTANT',
          content: `Error: ${errorMessage}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsStartingExecution(false);
    }
    
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLoadSession = async (selectedSessionId: string) => {
    setIsLoadingSession(true);
    try {
      const sessionData = await agentApi.getSession(
        selectedSessionId,
        buildCompactSessionPageRequest(),
      );
      await applySessionData(sessionData);
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleForkSession = async (selectedSessionId: string, forkFromMessageId?: number) => {
    setIsLoadingSession(true);
    try {
      const forkedSession = await agentApi.forkSession(
        selectedSessionId,
        forkFromMessageId ? { message_id: forkFromMessageId } : {}
      );
      await applySessionData(forkedSession);
      queryClient.invalidateQueries({ queryKey: ['conversation-sessions', agent.id, sessionScopeKey] });
      queryClient.invalidateQueries({ queryKey: ['latest-conversation-session', agent.id, sessionScopeKey] });
      toast.success(forkFromMessageId ? 'Conversation forked from message' : 'Conversation forked');
    } catch (error) {
      console.error('Failed to fork session:', error);
      toast.error('Failed to fork conversation');
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleNewConversation = () => {
    // Check if current session is already empty (no messages) - don't create another
    if (messages.length === 0 && sessionId === null) {
      pendingSubAgentEventsRef.current = {};
      clearPendingToolResults(pendingToolResultsRef.current);
      pendingToolRationaleRef.current = null;
      console.log('handleNewConversation - already in a fresh state, skipping');
      return;
    }
    
    console.log('Starting new conversation - setting userStartedNewConversation flag');
    userStartedNewConversation.current = true; // Prevent auto-loading
    pendingSubAgentEventsRef.current = {};
    clearPendingToolResults(pendingToolResultsRef.current);
    pendingToolRationaleRef.current = null;
    setMessages([]);
    setHasMoreMessages(false);
    setNextBeforeMessageId(null);
    setSessionId(null);
    setCurrentSessionData(null); // Clear session data
    setSnapshotView(null);
    setIsHistoricalSession(false); // Not a historical session
    setSelectedMessageIndex(null); // Clear selection
    setShowHistory(false);
    setPendingAttachments([]); // Clear any pending attachments
    resetExecution(); // Reset WebSocket execution state
  };

  // Handle restore from checkpoint
  const handleRestoreClick = (message: Message, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent message click
    setRestoreMessage(message);
    setRestoreDialogOpen(true);
  };

  const handleViewSnapshotClick = async (message: Message, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!sessionId || !message.dbMessageId) {
      setRestoreSnackbar({
        open: true,
        message: 'Cannot view snapshot: missing session or message data',
        severity: 'error',
      });
      return;
    }

    setSnapshotLoadingMessageId(message.id);
    try {
      const result = await agentApi.getMessageSnapshot(sessionId, message.dbMessageId);
      if (!result.has_snapshot || !result.system_snapshot) {
        setRestoreSnackbar({
          open: true,
          message: 'No snapshot is available for this checkpoint',
          severity: 'error',
        });
        return;
      }

      setSnapshotView({ messageId: message.id, snapshot: result.system_snapshot });
      if (isMobile) {
        setMobileTab(1);
      }
    } catch (error: any) {
      console.error('Failed to load message snapshot:', error);
      setRestoreSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load checkpoint snapshot',
        severity: 'error',
      });
    } finally {
      setSnapshotLoadingMessageId(null);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreMessage || !sessionId || !restoreMessage.dbMessageId) {
      setRestoreSnackbar({
        open: true,
        message: 'Cannot restore: missing session or message data',
        severity: 'error',
      });
      setRestoreDialogOpen(false);
      return;
    }

    setIsRestoring(true);
    try {
      const options: RestoreSnapshotOptions = {
        restore_experts: true,
        restore_schedules: true,
        restore_system_config: true,
        delete_extra: true, // Full rollback: remove items added after checkpoint
      };

      const result = await agentApi.restoreFromSnapshot(
        sessionId,
        restoreMessage.dbMessageId,
        options
      );

      if (result.status === 'success') {
        setRestoreSnackbar({
          open: true,
          message: `System restored to checkpoint! ${result.restoration_log.experts_updated.length} experts updated.`,
          severity: 'success',
        });

        // Refresh the system data
        if (onExpertCreated) {
          await onExpertCreated();
        }
        if (effectiveExecutionSystemId) {
          queryClient.invalidateQueries({ queryKey: ['ai-system', String(effectiveExecutionSystemId)] });
          queryClient.invalidateQueries({ queryKey: ['experts', effectiveExecutionSystemId] });
          queryClient.invalidateQueries({ queryKey: ['schedules', effectiveExecutionSystemId] });
        }
      } else {
        setRestoreSnackbar({
          open: true,
          message: result.message || 'Restoration failed',
          severity: 'error',
        });
      }
    } catch (error: any) {
      console.error('Failed to restore from snapshot:', error);
      setRestoreSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to restore from checkpoint',
        severity: 'error',
      });
    } finally {
      setIsRestoring(false);
      setRestoreDialogOpen(false);
      setRestoreMessage(null);
    }
  };

  const handleRestoreCancel = () => {
    setRestoreDialogOpen(false);
    setRestoreMessage(null);
  };

  // Handle clicking on a message to view its execution data
  const handleMessageClick = (index: number, message: Message) => {
    if (message.role === 'ASSISTANT' && message.executionId) {
      setSelectedMessageIndex(index);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' }}>
      {/* History Drawer - Render outside main container */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={showHistory}
        onClose={() => {
          console.log('Drawer onClose called');
          setShowHistory(false);
        }}
        sx={{
          '& .MuiDrawer-root': {
            position: 'fixed',
          },
          '& .MuiModal-root': {
            position: 'fixed',
            zIndex: 1300,
          },
        }}
        PaperProps={{
          sx: {
            width: 320,
            bgcolor: 'background.paper',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1300,
          }
        }}
        ModalProps={{
          keepMounted: false,
          style: {
            position: 'fixed',
            zIndex: 1300,
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Conversations
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewConversation}
              variant="outlined"
            >
              New
            </Button>
          </Box>

          {isLoadingSessions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Sessions List */}
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {sessionsData && sessionsData.results && sessionsData.results.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {sessionsData.results.map((session) => {
                      const sessionTitle = cleanMarkdownTitle(
                        session.title ||
                        session.first_message_preview ||
                        (session.last_message_preview?.content
                          ? session.last_message_preview.content.substring(0, 30) + '...'
                          : 'Untitled conversation')
                      );

                      return (
                      <ListItem key={session.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          onClick={() => handleLoadSession(session.id)}
                          selected={sessionId === session.id}
                          sx={{
                            borderRadius: 1,
                            border: 1,
                            borderColor: sessionId === session.id ? 'primary.main' : 'divider',
                            '&.Mui-selected': {
                              bgcolor: isDark ? 'primary.900' : 'primary.50',
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Badge badgeContent={session.message_count} color="primary">
                              <ChatBubbleOutlineIcon />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={sessionTitle}
                            secondary={
                              session.last_message_at
                                ? new Date(session.last_message_at).toLocaleString()
                                : new Date(session.created_at).toLocaleString()
                            }
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: sessionId === session.id ? 'bold' : 'normal',
                              noWrap: true,
                              dir: getTextDirection(sessionTitle),
                              sx: muiBidiTextSx,
                            }}
                            secondaryTypographyProps={{
                              variant: 'caption',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      );
                    })}
                  </List>
                ) : sessionsData && sessionsData.count > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No sessions on this page
                    </Typography>
                    <Button size="small" onClick={() => setSessionsPage(1)}>
                      Go to first page
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No previous conversations
                  </Alert>
                )}
              </Box>
              
              {/* Pagination - calculate actual page count based on results */}
              {sessionsData && (() => {
                // Calculate effective page count:
                // If current page has items but less than pageSize, it's the last page
                const resultsOnPage = sessionsData.results?.length || 0;
                const isLastPageWithContent = resultsOnPage > 0 && resultsOnPage < sessionsPageSize;
                const effectivePageCount = isLastPageWithContent 
                  ? sessionsPage  // Current page is the last with content
                  : Math.ceil(sessionsData.count / sessionsPageSize);
                
                return effectivePageCount > 1 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
                    <Pagination
                      count={effectivePageCount}
                      page={sessionsPage}
                      onChange={(_, page) => setSessionsPage(page)}
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                ) : null;
              })()}
            </>
          )}
        </Box>
      </Drawer>

      {/* Mobile Tab Navigation */}
      {isMobile && !chatOnly && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
          <Tabs 
            value={mobileTab} 
            onChange={(_, newValue) => setMobileTab(newValue)}
            variant="fullWidth"
            sx={{ minHeight: 36 }}
          >
            <Tab 
              icon={<SmartToyIcon sx={{ fontSize: 16 }} />} 
              label="Chat" 
              iconPosition="start"
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
            />
            <Tab
              icon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
              label="Workflow"
              iconPosition="start"
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
            />
          </Tabs>
        </Box>
      )}

      {/* Main Content - Resizable Split Pane on Desktop, Tabbed on Mobile */}
      <Box ref={containerRef} sx={{ 
        position: 'relative', 
        flex: 1, 
        minHeight: 0, 
        display: 'flex', 
        overflow: 'hidden',
        flexDirection: isMobile && !chatOnly ? 'column' : 'row',
        width: '100%',
        maxWidth: '100%',
      }}>
        {/* Left Side: Chat Interface */}
        <Box 
          sx={{ 
            width: chatOnly || isMobile ? '100%' : `${leftPanelWidth}%`, 
            minWidth: isMobile ? 'auto' : 300,
            height: '100%',
            overflow: 'hidden',
            flexShrink: 0,
            display: chatOnly ? 'flex' : isMobile ? (mobileTab === 0 ? 'flex' : 'none') : 'flex',
          }}
        >
            <Paper elevation={0} sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 0 }}>
              {/* Chat Header - Fixed at top */}
              <Box sx={{ px: { xs: 1, sm: 1.5 }, py: { xs: 0.5, sm: 0.75 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon sx={{ fontSize: { xs: 16, sm: 20 }, flexShrink: 0 }} />
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
                      {chatTitle}
                    </Typography>
                    {!headerActions && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                        {isSmallMobile ? agent.name : `${agent.name} • ${agent.model_name || agent.model?.name || agent.model?.model || 'Not set'}`}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 1, minWidth: 0, maxWidth: '72%' }}>
                    {headerActions && (
                      <Box sx={{ minWidth: 0, overflow: 'visible', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        {headerActions}
                      </Box>
                    )}
                    <ConversationDetailsButton
                      sessionId={sessionId || wsSessionId}
                      sessionData={currentSessionData}
                      messages={messages}
                      agentName={agent.name}
                      modelName={agent.model_name || agent.model?.name || agent.model?.model || 'Not set'}
                      systemName={system?.name || 'Not available'}
                      statusLabel={isChatBusy ? (isExecutionQueued ? 'Queued' : 'Running') : (sessionId ? 'Idle' : 'No active session')}
                      currentExecutionId={currentExecutionId}
                      liveSessionId={wsSessionId}
                      liveTotalTokens={totalTokens}
                      liveTotalCost={totalCost}
                      isExecuting={isExecuting}
                      buttonSx={{ p: { xs: 0.5, sm: 1 } }}
                      iconSx={{ fontSize: { xs: 18, sm: 24 } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setShowHistory(!showHistory)}
                      color={showHistory ? "primary" : "default"}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                    >
                      <HistoryIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNewConversation}
                      disabled={messages.length === 0}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                    >
                      <AddIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Messages Area - Independently scrollable */}
              <Box
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                p: { xs: 1, sm: 2 },
                minHeight: 0, // Important for flex scrolling
              }}>
                {(isLoadingSession || (shouldAutoLoadLatestSession && isLoadingLatestSession)) ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Loading conversation...
                    </Typography>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyStateTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {emptyStateHint}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {isLoadingOlderMessages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Loading earlier messages
                        </Typography>
                      </Box>
                    )}
                    {!isLoadingOlderMessages && hasMoreMessages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<KeyboardArrowUpIcon sx={{ fontSize: 18 }} />}
                          onClick={loadOlderMessages}
                          sx={{ minHeight: 28, px: 1, textTransform: 'none' }}
                        >
                          Load earlier
                        </Button>
                      </Box>
                    )}
                    {groupedMessages.map((item, index) => {
                    if ('type' in item && item.type === 'tool_group') {
                      return <ToolCallGroup key={item.id} toolMsgs={item.msgs} isDark={isDark} />;
                    }
                    const message = item as Message;
                    const isStatusMessage = message.role === 'ASSISTANT_STATUS';
                    const messageDirection = getTextDirection(message.content);
                    const forkFromMessageId = message.dbMessageId;
                    return (
                    <Box
                      key={message.id}
                      onClick={() => handleMessageClick(index, message)}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        gap: { xs: 0.75, sm: 1 },
                        alignItems: 'flex-start',
                        cursor: message.role === 'ASSISTANT' && message.executionId ? 'pointer' : 'default',
                        position: 'relative',
                        minWidth: 0,
                        overflow: 'hidden',
                        '&:hover': message.role === 'ASSISTANT' && message.executionId ? {
                          '& .message-paper': {
                            boxShadow: 2,
                          }
                        } : {},
                      }}
                    >
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: message.role === 'USER'
                            ? 'primary.main'
                            : isStatusMessage
                              ? 'action.disabledBackground'
                              : 'secondary.main',
                          color: 'white',
                          flexShrink: 0,
                          border: selectedMessageIndex === index && message.role === 'ASSISTANT' ? '2px solid' : 'none',
                          borderColor: 'primary.main',
                        }}
                      >
                        {message.role === 'USER' ? <PersonIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> : <SmartToyIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                          </Box>

                      {/* Message Content */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" fontWeight="bold">
                            {message.role === 'USER' ? 'You' : assistantLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {message.timestamp.toLocaleTimeString()}
                          </Typography>
                          {isStatusMessage && (
                            <Chip
                              label="Working"
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.6rem' }}
                            />
                          )}
                          {selectedMessageIndex === index && message.role === 'ASSISTANT' && (
                            <Chip 
                              label="Viewing" 
                              size="small" 
                              color="primary" 
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                          {sessionId && forkFromMessageId && (
                            <Tooltip title="Fork from this message">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleForkSession(sessionId, forkFromMessageId);
                                }}
                                disabled={isLoadingSession}
                                aria-label="Fork from this message"
                                sx={{
                                  p: 0.25,
                                  ml: 0.25,
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                  }
                                }}
                              >
                                <CallSplitIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Checkpoint indicator and restore button for USER messages with snapshots */}
                          {enableSnapshots && message.role === 'USER' && message.hasSnapshot && (
                            <>
                              <Tooltip title={`Checkpoint (${message.snapshotMode})`}>
                                <Chip 
                                  icon={<CheckpointIcon sx={{ fontSize: '0.85rem !important' }} />}
                                  label="Checkpoint" 
                                  size="small" 
                                  color="success"
                                  sx={{ height: 20, fontSize: '0.65rem', ml: 1, bgcolor: 'success.main', color: 'white', '& .MuiChip-icon': { ml: 0.5, color: 'white' } }}
                                />
                              </Tooltip>
                              <Tooltip title="View checkpoint snapshot">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleViewSnapshotClick(message, e)}
                                  disabled={snapshotLoadingMessageId === message.id}
                                  sx={{
                                    p: 0.25,
                                    ml: 0.5,
                                    '&:hover': {
                                      bgcolor: 'primary.main',
                                      color: 'primary.contrastText',
                                    }
                                  }}
                                >
                                  {snapshotLoadingMessageId === message.id ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <VisibilityIcon sx={{ fontSize: 14 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Restore to checkpoint">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRestoreClick(message, e)}
                                  sx={{
                                    p: 0.25,
                                    ml: 0.25,
                                    '&:hover': {
                                      bgcolor: 'warning.main',
                                      color: 'warning.contrastText',
                                    }
                                  }}
                                >
                                  <RestoreIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                        <Paper
                          className="message-paper"
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            bgcolor: message.role === 'USER'
                              ? (isDark ? 'primary.900' : 'primary.50')
                              : isStatusMessage
                                ? (isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.06)')
                                : (isDark ? 'grey.800' : 'grey.50'),
                            border: selectedMessageIndex === index && message.role === 'ASSISTANT' ? '2px solid' : 'none',
                            borderColor: 'primary.main',
                            transition: 'all 0.2s',
                            overflow: 'hidden',
                            maxWidth: '100%',
                          }}
                            >
                          <Box
                            dir={messageDirection}
                            sx={{
                              ...muiBidiTextSx,
                              overflow: 'hidden',
                              maxWidth: '100%',
                              '& p': { margin: '0.5em 0', wordBreak: 'break-word' },
                              '& pre': { 
                                direction: 'ltr',
                                unicodeBidi: 'isolate',
                                textAlign: 'left',
                                backgroundColor: isDark ? '#2d2d2d' : '#f5f5f5',
                                padding: '12px',
                                borderRadius: '4px',
                                overflow: 'auto',
                                fontSize: '0.85em',
                                maxWidth: '100%',
                                whiteSpace: 'pre',
                              },
                              '& code': {
                                direction: 'ltr',
                                unicodeBidi: 'isolate',
                                backgroundColor: isDark ? '#2d2d2d' : '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '0.9em',
                                fontFamily: 'monospace',
                                wordBreak: 'break-word',
                              },
                              '& pre code': {
                                backgroundColor: 'transparent',
                                padding: 0,
                                wordBreak: 'normal',
                              },
                              '& ul, & ol': {
                                paddingInlineStart: '1.5em',
                                margin: '0.5em 0',
                              },
                              '& h1, & h2, & h3, & h4, & h5, & h6': {
                                margin: '0.75em 0 0.5em',
                                fontWeight: 600,
                              },
                              '& blockquote': {
                                borderInlineStart: '3px solid #ddd',
                                paddingInlineStart: '1em',
                                margin: '0.5em 0',
                                color: 'text.secondary',
                              },
                              '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                margin: '0.5em 0',
                              },
                              '& th, & td': {
                                border: '1px solid',
                                borderColor: isDark ? 'grey.700' : '#ddd',
                                padding: '8px',
                                textAlign: 'start',
                              },
                              '& th': {
                                backgroundColor: isDark ? 'grey.800' : '#f5f5f5',
                                fontWeight: 600,
                              },
                            }}
                          >
                            {message.role === 'USER' ? (
                              <AttachmentMessage content={message.content} />
                            ) : isStatusMessage ? (
                              <Typography variant="body2" color="text.secondary">
                                {message.content}
                              </Typography>
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeHighlight, rehypeRaw]}
                              >
                                {message.content}
                              </ReactMarkdown>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                    );
                  })}
                  </>
                    )}

                {/* Tool Confirmation Dialog - shown inline in chat when a dangerous tool requires confirmation */}
                {pendingConfirmation && (
                  <Box sx={{ mb: 2 }}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        border: '2px solid',
                        borderColor: 'warning.main',
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 152, 0, 0.04)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                        <Typography variant="subtitle1" fontWeight={600} color="warning.dark">
                          Confirmation Required
                        </Typography>
                        <Chip
                          label={`Danger Level: ${pendingConfirmation.dangerLevel}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        The tool <strong>{pendingConfirmation.toolName}</strong> requires your confirmation before execution.
                      </Typography>
                      {Object.keys(pendingConfirmation.toolArgs).length > 0 && (
                        <Box
                          sx={{
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'action.hover',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            overflow: 'auto',
                            maxHeight: 120,
                          }}
                        >
                          {JSON.stringify(pendingConfirmation.toolArgs, null, 2)}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          onClick={() => {
                            confirmTool(
                              pendingConfirmation.executionId,
                              pendingConfirmation.toolName,
                              false,
                              pendingConfirmation.toolCallId
                            );
                          }}
                        >
                          Deny
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          onClick={() => {
                            confirmTool(
                              pendingConfirmation.executionId,
                              pendingConfirmation.toolName,
                              true,
                              pendingConfirmation.toolCallId
                            );
                          }}
                        >
                          Confirm Execution
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Loading Indicator */}
                {isChatBusy && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">
                        {isExecutionQueued
                          ? `${assistantLabel} is queued...`
                          : isStartingExecution && !isExecuting
                            ? `${assistantLabel} is starting...`
                            : `${assistantLabel} is thinking...`}
                      </Typography>
                      {/* WebSocket connection indicator */}
                      {wsConnected ? (
                        <WifiIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <WifiOffIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      )}
                    </Box>
                    {/* Expert progress */}
                    {expertProgress.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {expertProgress.map((expert, index) => (
                          <Chip
                            key={`${expert.expert_id}-${expert.iteration ?? 'main'}-${index}`}
                            label={expert.iteration ? `${expert.expert_name} #${expert.iteration}` : expert.expert_name}
                            size="small"
                            color={
                              expert.status === 'completed' ? 'success' :
                              expert.status === 'running' ? 'primary' :
                              expert.status === 'error' ? 'error' : 'default'
                            }
                            variant={expert.status === 'running' ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area - Fixed at bottom */}
              <Box sx={{ p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                {/* Pending Attachments Preview */}
                {pendingAttachments.length > 0 && (
                  <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {pendingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        icon={getAttachmentIcon(attachment.attachment_type)}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                              {attachment.original_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({formatFileSize(attachment.size)})
                            </Typography>
                          </Box>
                        }
                        onDelete={() => handleRemoveAttachment(attachment.id)}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          maxWidth: 280,
                          '& .MuiChip-deleteIcon': {
                            fontSize: 18,
                            marginRight: '4px',
                            color: 'action.active',
                            '&:hover': {
                              color: 'error.main',
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                  accept=".txt,.md,.json,.csv,.py,.js,.ts,.jsx,.tsx,.html,.css,.xml,.yaml,.yml,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.svg"
                />
                
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start', flexDirection: 'column' }}>
                  {/* Voice overlay */}
                  {isVoiceOn && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <VoiceOverlay voiceState={voiceState} interimTranscript={interimTranscript} />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start', width: '100%' }}>
                  {/* Attach File Button */}
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isChatBusy || isUploading || systemBuilderAccess.disabled}
                    size={isSmallMobile ? 'small' : 'medium'}
                    sx={{ mt: 0.5, p: { xs: 0.5, sm: 1 } }}
                    title={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : 'Attach file'}
                  >
                    {isUploading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AttachFileIcon />
                    )}
                  </IconButton>
                  
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size={isSmallMobile ? 'small' : 'medium'}
                    placeholder={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : isVoiceOn ? "Recording voice message..." : (pendingAttachments.length > 0 
                      ? "Add a message about your files..." 
                      : isSmallMobile ? `Ask ${assistantLabel}...` : inputPlaceholder)}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isChatBusy || systemBuilderAccess.disabled}
                    inputProps={{ dir: getTextDirection(inputMessage) }}
                    sx={{
                      '& .MuiInputBase-root': {
                        py: { xs: 0.75, sm: 1 },
                      },
                      '& .MuiInputBase-input': {
                        ...muiBidiTextSx,
                        direction: getTextDirection(inputMessage),
                      },
                    }}
                  />

                  {/* Mic button */}
                  <MicButton
                    voiceState={voiceState}
                    isRecordingSupported={isRecordingSupported}
                    disabled={voiceButtonDisabled}
                    disabledReason={voiceButtonDisabledReason}
                    onClick={toggleVoice}
                    size={isSmallMobile ? 'small' : 'medium'}
                    sx={{ mt: 0.5, flexShrink: 0 }}
                  />

                  {/* Voice settings */}
                  {isTTSSupported && (
                    <VoiceSettings
                      ttsVoice={ttsVoice}
                      onVoiceChange={setTtsVoice}
                      voiceRate={voiceRate}
                      onRateChange={setVoiceRate}
                      disabled={isVoiceOn}
                      sx={{ mt: 0.5 }}
                    />
                  )}

                  {isExecuting ? (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleStopExecution}
                      disabled={!(currentExecutionId || currentQueuedTaskId)}
                      size={isSmallMobile ? 'small' : 'medium'}
                      sx={{
                        minWidth: { xs: 60, sm: 100 },
                        height: { xs: 40, sm: 56 },
                        borderWidth: 2,
                        px: { xs: 1, sm: 2 },
                        '&:hover': { borderWidth: 2 },
                      }}
                      startIcon={!isSmallMobile && <StopIcon />}
                    >
                      {isSmallMobile ? <StopIcon /> : currentExecutionId ? 'Stop' : 'Cancel'}
                    </Button>
                  ) : (
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={isStartingExecution || (!inputMessage.trim() && pendingAttachments.length === 0) || systemBuilderAccess.disabled}
                      size={isSmallMobile ? 'small' : 'medium'}
                      sx={{ p: { xs: 0.5, sm: 1 } }}
                      title={systemBuilderAccess.disabled ? systemBuilderAccess.blockedMessage : 'Send'}
                    >
                      {isStartingExecution ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Resizable Divider/Handle - Hidden on Mobile */}
          {!isMobile && !chatOnly && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: 8,
                cursor: 'col-resize',
                backgroundColor: isDragging ? 'primary.main' : 'transparent',
                transition: isDragging ? 'none' : 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&::after': {
                  content: '""',
                  width: 4,
                  height: 40,
                  backgroundColor: isDragging ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  transition: isDragging ? 'none' : 'background-color 0.2s',
                },
                '&:hover::after': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
          )}

          {/* Right Side: Insights & System Overview */}
          {!chatOnly && (
          <Box 
            sx={{ 
              flex: 1, 
              minWidth: isMobile ? 'auto' : 300,
              width: isMobile ? '100%' : 'auto',
              height: '100%',
              overflow: 'hidden',
              display: isMobile ? (mobileTab === 1 ? 'flex' : 'none') : 'flex',
              margin: 0,
              padding: 0,
            }}
          >
            <AIBuilderInsightsPanel 
                system={system}
                sessionData={snapshotView ? { ...currentSessionData, system_snapshot: snapshotView.snapshot } : currentSessionData}
                isHistoricalSession={isHistoricalSession}
                selectedMessageId={snapshotView?.messageId ?? null}
                onExitSnapshot={() => setSnapshotView(null)}
                isMobile={isMobile}
                // Pass canvas props for interactive canvas in system preview
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onAutoLayout={onAutoLayout}
                expertsData={expertsData}
                addExpertToCanvas={addExpertToCanvas}
                setExpertFormOpen={setExpertFormOpen}
                selectedNode={selectedNode}
                setSelectedNode={setSelectedNode}
                setNodes={setNodes}
                setEdges={setEdges}
                refetchExperts={refetchExperts}
                validationIssues={validationIssues}
                onSaveCanvas={onSaveCanvas}
                isSavingCanvas={isSavingCanvas}
                onRunCanvas={onRunCanvas}
                onStopCanvas={onStopCanvas}
                isRunningCanvas={isRunningCanvas}
                isStoppingCanvas={isStoppingCanvas}
                canRunCanvas={canRunCanvas}
                runDisabledReason={runDisabledReason}
                expertRunStates={expertRunStates}
                onViewExpertRun={onViewExpertRun}
                nodeTypes={nodeTypes}
                onExpertUpdate={onExpertUpdate}
              />
          </Box>
          )}
      </Box>

      {/* Restore Checkpoint Dialog */}
      {enableSnapshots && <Dialog
        open={restoreDialogOpen}
        onClose={handleRestoreCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="warning" />
          Restore to Checkpoint
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restore the AI system to this checkpoint?
          </DialogContentText>
          {restoreMessage && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Checkpoint from: {restoreMessage.timestamp.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Snapshot type: {restoreMessage.snapshotMode}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                "{restoreMessage.content.substring(0, 150)}{restoreMessage.content.length > 150 ? '...' : ''}"
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will fully restore the system to this checkpoint. Experts, schedules, and settings added after this point will be removed. You can undo by restoring to a newer checkpoint.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestoreCancel} disabled={isRestoring}>
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreConfirm} 
            variant="contained" 
            color="warning"
            disabled={isRestoring}
            startIcon={isRestoring ? <CircularProgress size={16} /> : <RestoreIcon />}
          >
            {isRestoring ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>}

      {/* Restore Result Snackbar */}
      <Snackbar
        open={restoreSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setRestoreSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setRestoreSnackbar(prev => ({ ...prev, open: false }))}
          severity={restoreSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {restoreSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIBuilder;
